<template>
  <div
    v-loading.fullscreen.lock="isSubmitting"
    :element-loading-text="$t('Submitting...')"
    class="company-profile-form-page page-content">
    <el-row class="page-header -with-back-button" type="flex" justify="space-between">
      <el-col :span="24">
        <el-button
          type="text"
          icon="el-icon-back"
          class="back-button"
          @click.prevent="goBack">
        </el-button>
        <h2 class="page-title">แก้ไขข้อมูลบริษัทสำหรับรับชำระ</h2>
      </el-col>
    </el-row>

    <el-form
      v-if="profile"
      :model="profileForm"
      :rules="profileFormRules"
      ref="profileForm"
      label-width="120px"
      label-position="top"
      @submit.native.prevent="submitForm('profileForm')"
      :disabled="isSubmitting"
      class="profile-form profile-detail-info detail-form company-detail-info">
      <el-row class="detail-section">
        <el-row>
          <el-col :span="24">
            <h3 class="section-title">ข้อมูลบริษัทสำหรับรับชำระ</h3>
          </el-col>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10" class="label">
              ชื่อบริษัท
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="10">
              <div class="value-text">{{ profile.name }}</div>
            </el-col>
          </el-row>
        </el-row>
        <el-row class="logo-uploader-holder">
          <el-col :span="24">
            <el-form-item label="โลโก้บริษัท" prop="logo">
              <div class="logo-holder" v-if="profileForm.logo">
                <img :src="profileForm.logo" class="logo"/>
              </div>
              <el-upload
                class="company-logo-uploader"
                :action="uploadUrl"
                drag
                :show-file-list="false"
                :http-request="handleHttpRequest"
                :on-success="handleLogoSuccess"
                :on-error="handleLogoFail"
                :before-upload="beforeLogoUpload">
                <div class="upload-instruction" v-loading="isUploading">
                  <h3>อัพโหลดโลโก้บริษัท</h3>
                  <i class="el-icon-upload logo-uploader-icon"></i>
                </div>
              </el-upload>
              <el-button
                type="danger"
                icon="el-icon-delete"
                @click="removeLogo"
                v-if="profileForm.logo"
                class="remove-logo">ลบโลโก้บริษัท</el-button>
              <div class="upload-instruction-hint">
                ขนาดขั้นต่ำ 500px x 500px และต้องเป็นไฟล์นามสกุล JPG, PNG, GIF
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-row>

      <el-row class="detail-section">
        <el-row>
          <el-col :span="24">
            <h3 class="section-title">ที่อยู่บริษัท <sup class="required">**</sup></h3>
          </el-col>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10">
              <el-form-item label="" prop="use_company_address">
                <el-checkbox
                  v-model="profileForm.use_company_address"
                  @change="toggleUsingCompanyAddress"
                  id="profileForm.use_company_address">ใช้ที่อยู่เดียวกับรายละเอียดหลักของบริษัท</el-checkbox>
              </el-form-item>
            </el-col>
          </el-row>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10">
              <el-form-item label="ที่อยู่" prop="address.line_1">
                <el-input
                  v-model="profileForm.address.line_1"
                  :disabled="profileForm.use_company_address"
                  id="profileForm.address.line_1"
                  placeholder="กรอกที่อยู่"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10">
              <el-form-item label="ตำบล/แขวง" prop="address.district">
                <el-input
                  v-model="profileForm.address.district"
                  :disabled="profileForm.use_company_address"
                  id="profileForm.address.district"
                  placeholder="กรอกตำบล/แขวง"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="10" :offset="1">
              <el-form-item label="อำเภอ/เขต" prop="address.city">
                <el-input
                  v-model="profileForm.address.city"
                  :disabled="profileForm.use_company_address"
                  id="profileForm.address.city"
                  placeholder="กรอกอำเภอ/เขต"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10">
              <el-form-item label="จังหวัด" prop="address.province">
                <el-select
                  v-model="profileForm.address.province"
                  :disabled="profileForm.use_company_address"
                  id="profileForm.address.province">
                  <el-option
                    v-for="province in provinceList"
                    :key="province"
                    :label="province"
                    :value="province">
                  </el-option>
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="10" :offset="1">
              <el-form-item label="รหัสไปรษณีย์" prop="address.postal_code">
                <el-input
                  v-model="profileForm.address.postal_code"
                  :disabled="profileForm.use_company_address"
                  id="profileForm.address.postal_code"
                  @change="updatePostCode"
                  ref="profileForm.address.postal_code"
                  @input.native="handleInput($event, 'profileForm.address.postal_code', 'postcode')"
                  placeholder="กรอกรหัสไปรษณีย์"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="10">
              <el-form-item label="โทรศัพท์" prop="address.tel">
                <el-input
                  v-model="profileForm.address.tel"
                  :disabled="profileForm.use_company_address"
                  id="profileForm.address.tel"
                  placeholder="กรอกโทรศัพท์"></el-input>
              </el-form-item>
            </el-col>
            <el-col :span="10" :offset="1">
              <el-form-item label="โทรสาร" prop="address.fax">
                <el-input
                  v-model="profileForm.address.fax"
                  :disabled="profileForm.use_company_address"
                  id="profileForm.address.fax"
                  placeholder="กรอกโทรสาร"></el-input>
              </el-form-item>
            </el-col>
          </el-row>
        </el-row>
      </el-row>

      <el-row class="detail-section">
        <el-row>
          <el-col :span="24">
            <h3 class="section-title">Bill Payment</h3>
          </el-col>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="7" class="label">
              COMP CODE
            </el-col>
            <el-col :span="7" class="label">
              เลขที่บัญชี
            </el-col>
            <el-col :span="7" class="label">
              Biller ID
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="7">
              <div class="value-text">{{ compCodeFormatter(profile.bill_payment_comp_code) }}</div>
            </el-col>
            <el-col :span="7">
              <div class="value-text">{{ accountNoFormatter(profile.bill_payment_account_no) }}</div>
            </el-col>
            <el-col :span="7">
              <div class="value-text">{{ billerIdFormatter(profile.bill_payment_biller_id) }}</div>
            </el-col>
          </el-row>
        </el-row>
      </el-row>

      <el-row class="detail-section">
        <el-row>
          <el-col :span="24">
            <h3 class="section-title">ข้อมูลสำหรับบาร์โค้ดและ QR code</h3>
          </el-col>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10" class="label">
              {{ profile.barcode_qrcode_biller_id_tax_id !== '' &&
              profile.barcode_qrcode_biller_id_tax_id !== null ? 'Biller ID' : 'หมายเลขบัญชี' }}
            </el-col>
            <el-col :span="10" :offset="1" class="label">
              Suffix
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="10">
              <div class="value-text">
              {{
                profile.barcode_qrcode_biller_id_tax_id !== '' &&
                profile.barcode_qrcode_biller_id_tax_id !== null ?
                  billerIdFormatter(profile.barcode_qrcode_biller_id_tax_id) :
                  accountNoFormatter(profile.barcode_qrcode_account_no)
              }}
              </div>
            </el-col>
            <el-col :span="2" :offset="1">
              <div class="value-text">
              {{
                profile.barcode_qrcode_biller_id_tax_id !== '' &&
                profile.barcode_qrcode_biller_id_tax_id !== null ?
                profile.barcode_qrcode_biller_id_suffix :
                profile.barcode_qrcode_account_no_suffix
              }}
              </div>
            </el-col>
          </el-row>
        </el-row>
      </el-row>

      <el-row class="detail-section">
        <el-row>
          <el-col :span="24">
            <h3 class="section-title">Business Type</h3>
          </el-col>
        </el-row>
        <el-row class="detail-row">
          <el-col :span="24">
            <div class="value-text">
              {{ profileBusinessType }}
            </div>
          </el-col>
        </el-row>
      </el-row>

      <el-row class="detail-section">
        <el-row>
          <el-col :span="24">
            <h3 class="section-title">วิธีการชำระเงิน</h3>
          </el-col>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10">
              <div class="value-text">
              {{ profile.payment_type === 'CHEQUE_OR_CASH' ? 'เช็ค/เงินสด' : 'เงินสดอย่างเดียว' }}
              </div>
            </el-col>
          </el-row>
        </el-row>
      </el-row>

      <el-row class="detail-section">
        <el-row>
          <el-col :span="24">
            <h3 class="section-title">Charge Type</h3>
          </el-col>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10" class="label">
              {{ profileForm.charge_payer ? 'Payer' : 'Biller' }}
            </el-col>
          </el-row>
          <!-- <el-row>
            <el-col :span="10">
              <div class="value-text">
              {{ profile.charge_payer ? 'Yes' : 'No' }}
              </div>
            </el-col>
          </el-row> -->
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10" class="label">
              IB/IR
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="10">
              <div class="value-text">{{ profile.ir_br }}</div>
            </el-col>
          </el-row>
        </el-row>
      </el-row>

      <el-row class="detail-section">
        <el-row>
          <el-col :span="24">
            <h3 class="section-title">Account Reference</h3>
          </el-col>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10" class="label">
              ชื่อบัญชี
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="10">
              <div class="value-text">{{ profile.account_name }}</div>
            </el-col>
          </el-row>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10" class="label">
              Label (Ref. 1)
            </el-col>
            <el-col :span="10" :offset="1" class="label">
              Label (Ref. 2)
            </el-col>
          </el-row>
          <el-row>
            <el-col :span="10">
              <div class="value-text">{{ profile.label_ref1 }}</div>
            </el-col>
            <el-col :span="10" :offset="1">
              <div class="value-text">{{ profile.label_ref2 }}</div>
            </el-col>
          </el-row>
        </el-row>
      </el-row>

      <el-row class="detail-section" v-if="profileForm.allow_other_banks">
        <el-row>
          <el-col :span="24">
            <h3 class="section-title">ข้อมูลบัญชีธนาคารอื่นๆที่จะปรากฎบน Pay-in Slip (สูงสุด 6 บัญชี)</h3>
            <div class="other-bank-upload-instruction">** ขนาดโลโก้ไม่เกิน 300px x 300px และต้องเป็นไฟล์นามสกุล JPG, PNG, GIF</div>
          </el-col>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="24">
              <div class="other-bank-list"
                v-for="(bankInfo, index, item) in profileForm.other_banks">
                <div class="other-bank-info form" v-if="isBillerAdmin">
                  <el-row>
                    <el-col :span="1">
                      <el-form-item
                        label=""
                        :prop="'other_banks.' + index +  '.is_active'">
                        <el-checkbox
                          @change="markBankInfoActive(index)"
                          v-model="bankInfo.is_active">{{ index + 1 }}.</el-checkbox>
                      </el-form-item>
                    </el-col>
                    <el-col :span="2">
                      <div class="other-bank-logo-holder" v-if="!bankInfo.is_active || (bankInfo.account_logo && bankInfo.is_active)">
                        <img :src="bankInfo.account_logo" class="logo" v-if="bankInfo.account_logo"/>
                      </div>
                      <el-upload
                        v-if="!bankInfo.account_logo && bankInfo.is_active"
                        class="other-bank-logo-uploader"
                        :action="uploadUrl"
                        drag
                        :show-file-list="false"
                        :http-request="handleOtherBankHttpRequest"
                        :on-success="handleOtherBankLogoSuccessList[index]"
                        :on-error="handleLogoFail"
                        :before-upload="beforeLogoUpload">
                        <div class="upload-instruction" v-loading="isUploading">
                          <i class="el-icon-upload logo-uploader-icon"></i>
                        </div>
                      </el-upload>
                      <el-button
                        type="danger"
                        icon="el-icon-delete"
                        @click="removeOtherBankLogo(index)"
                        v-if="bankInfo.account_logo && bankInfo.is_active"
                        class="remove-logo">ลบโลโก้</el-button>
                    </el-col>
                    <el-col :span="21">
                      <el-row>
                        <el-col :span="6" :offset="1">
                          <el-form-item label="ชื่อธนาคาร/ตัวแทนรับชำระ"
                            :prop="'other_banks.' + index +  '.account_name'"
                            :rules="{ validator: checkOtherBankItem, trigger: 'blur' }">
                            <el-input
                              v-model="bankInfo.account_name"
                              :disabled="!bankInfo.is_active"
                              placeholder="กรอกชื่อบัญชี"></el-input>
                          </el-form-item>
                        </el-col>
                        <el-col :span="6" :offset="1">
                          <el-form-item label="เลขที่บัญชี"
                            :key="'other_banks.' + index +  '.account_number'"
                            :prop="'other_banks.' + index +  '.account_number'"
                            :rules="{ validator: checkOtherBankItem, trigger: 'blur' }">
                            <el-input
                              v-model="bankInfo.account_number"
                              :disabled="!bankInfo.is_active"
                              :id="'profileForm.other_banks.' + index + '.account_number'"
                              :ref="'profileForm.other_banks.' + index + '.account_number'"
                              @input.native="handleInput($event, 'profileForm.other_banks.' + index + '.account_number', 'accNumberOnly')"
                              placeholder="กรอกเลขที่บัญชี"></el-input>
                          </el-form-item>
                        </el-col>
                        <el-col :span="6" :offset="1">
                          <el-form-item label="COMP CODE"
                            :key="'other_banks.' + index +  '.comcode'"
                            :prop="'other_banks.' + index +  '.comcode'">
                            <el-input
                              v-model="bankInfo.comcode"
                              :disabled="!bankInfo.is_active"
                              :id="'profileForm.other_banks.' + index + '.comcode'"
                              placeholder="กรอก COMP CODE"></el-input>
                          </el-form-item>
                        </el-col>
                      </el-row>
                      <el-row>
                        <el-col :span="6" :offset="1">
                          <el-form-item label="ค่าธรรมเนียม"
                                        :key="'other_banks.' + index +  '.service_fee'"
                                        :prop="'other_banks.' + index +  '.service_fee'">
                            <el-input
                              v-model="bankInfo.service_fee"
                              :disabled="!bankInfo.is_active"
                              :id="'profileForm.other_banks.' + index + '.service_fee'"
                              placeholder="กรอกค่าธรรมเนียม"></el-input>
                          </el-form-item>
                        </el-col>
                        <el-col :span="6" :offset="1">
                          <el-form-item label="Service Code"
                                        :key="'other_banks.' + index +  '.service_code'"
                                        :prop="'other_banks.' + index +  '.service_code'" v-if="isBBLBank(bankInfo.account_name)"
                                        :rules="{ validator: checkOtherBankItem, trigger: 'blur' }">
                            <el-input
                              v-model="bankInfo.service_code"
                              :disabled="!bankInfo.is_active"
                              :id="'profileForm.other_banks.' + index + '.service_code'"
                              placeholder="กรอก Service Code"></el-input>
                          </el-form-item>
                        </el-col>
                        <el-col :span="6" :offset="1">
                          <el-form-item label="รหัสสาขา"
                                        :key="'other_banks.' + index +  '.branch_code'"
                                        :prop="'other_banks.' + index +  '.branch_code'" v-if="isBBLBank(bankInfo.account_name)">
                            <el-input
                              v-model="bankInfo.branch_code"
                              :disabled="!bankInfo.is_active"
                              :id="'profileForm.other_banks.' + index + '.branch_code'"
                              :ref="'profileForm.other_banks.' + index + '.branch_code'"
                              @input.native="handleInput($event, 'profileForm.other_banks.' + index + '.branch_code', 'numberOnly')"
                              placeholder="กรอกรหัสสาขา"></el-input>
                          </el-form-item>
                        </el-col>
                      </el-row>
                    </el-col>
                  </el-row>
                </div>
                <div class="other-bank-info" v-else>
                  <div class="logo">
                    {{ bankInfo.account_logo }}
                  </div>
                  <div class="text">
                    {{ getOtherBankInfoSummaryText(bankInfo) }}
                  </div>
                </div>
              </div>
            </el-col>
          </el-row>
        </el-row>
      </el-row>

      <el-row class="detail-section">
        <el-row>
          <el-col :span="24">
            <h3 class="section-title">การแจ้งเตือน</h3>
          </el-col>
        </el-row>
        <el-row class="detail-row">
          <el-col :span="24">
            <el-form-item
              label=""
              ref="notification_msg"
              prop="notification_msg">
              <el-row>
                <el-col :span="24">
                  <el-radio
                    v-model="profileForm.notification_msg_type"
                    label="DATE"
                    @change="toggleUsingNotificationMsgValue"
                    id="profileForm.notification_msg_type_1">
                    <el-row>
                      <el-col :span="8">กรุณาชำระภายในวันที่</el-col>
                      <el-col :span="5" :offset="1">
                        <el-select
                          v-model="profileForm.notification_msg_date"
                          :disabled="profileForm.notification_msg_type === 'CUSTOM'"
                          @change="toggleUsingNotificationMsgValue"
                          style="width:100px;text-align:center;"
                          id="profileForm.notification_msg_date">
                          <el-option
                            v-for="day in notificationDayList"
                            :key="day"
                            :label="day"
                            :value="day">
                          </el-option>
                        </el-select>
                      </el-col>
                      <el-col :span="5" :offset="1">
                        ของทุกเดือน
                      </el-col>
                    </el-row>
                  </el-radio>
                </el-col>
              </el-row>
              <el-row style="margin-top: 20px;">
                <el-col :span="24">
                  <el-radio
                    v-model="profileForm.notification_msg_type"
                    label="CUSTOM"
                    @change="toggleUsingNotificationMsgValue"
                    id="profileForm.notification_msg_type_2">
                    _
                  </el-radio>
                  <el-input
                    v-model="profileForm.notification_msg_custom"
                    :disabled="profileForm.notification_msg_type !== 'CUSTOM'"
                    id="profileForm.notification_msg_custom"
                    :maxlength="100"
                    style="width:300px;"
                    placeholder="กรอกข้อความแจ้งเตือนที่ต้องการ"></el-input>
                </el-col>
              </el-row>
            </el-form-item>
          </el-col>
        </el-row>
      </el-row>

      <!-- <el-row class="detail-section">
        <el-row>
          <el-col :span="24">
            <h3 class="section-title">ตัวเลือกในการพิมพ์</h3>
          </el-col>
        </el-row>
        <el-row class="detail-row">
          <el-row>
            <el-col :span="10">
              {{ profile.printing_option === 'COLOR' ? 'พิมพ์สี' : 'พิมพ์ขาวดำ' }}
            </el-col>
          </el-row>
        </el-row>
      </el-row> -->

      <el-row>
        <el-col :span="24">
          <el-form-item class="button-bar">
            <el-button
              type="primary"
              plain
              class="cancel-btn"
              @click="goBack"
              id="profileForm.cancelButton">{{ 'Cancel' | translate }}</el-button>
            <div class="submit-button-group">
              <el-button
                type="primary"
                id="profileForm.submitButton"
                icon="el-icon-edit"
                native-type="submit">{{ 'Save and continue' | translate }}</el-button>
            </div>
          </el-form-item>
        </el-col>
      </el-row>

    </el-form>
  </div>
</template>

<script>
  /* eslint no-param-reassign: 0 */
  import helpers from '@/libs/helpers';
  import _ from 'lodash';
  import { createMask } from 'imask';
  import XRegExp from 'xregexp';
  /**
   * Biller Profile Form view
   */
  const BillerProfileFormView = {
    name: 'biller-profile-form',
    props: ['profileId'],
    data() {
      return {
        isSubmitting: false,
        isUploading: false,
        currentOtherBankUploading: '',
        isSetupUpdateData: false,
        isUpdated: true,
        useDefaultSCBNoti: true,
        dayList: [],
        profileFormRules: {
          name: [
            { required: true, message: 'กรุณากรอกชื่อบริษัท', trigger: 'blur' },
          ],
          'address.line_1': [
            { required: true, message: 'กรุณากรอกที่อยู่บริษัท', trigger: 'blur' },
          ],
          'address.district': [
            { required: true, message: 'กรุณากรอกตำบล/แขวง', trigger: 'blur' },
          ],
          'address.city': [
            { required: true, message: 'กรุณากรอกอำเภอ/เขต', trigger: 'blur' },
          ],
          'address.province': [
            { required: true, message: 'กรุณาเลือกจังหวัด', trigger: 'blur' },
          ],
          'address.postal_code': [
            { required: true, message: 'กรุณากรอกรหัสไปรษณีย์', trigger: 'blur' },
          ],
          'address.tel': [
            { required: true, message: 'กรุณากรอกหมายเลขโทรศัพท์', trigger: 'blur' },
            // { type: 'string',
            // pattern: new XRegExp('^02-\\d{3}-\\d{4}$|^0[3-5|7]\\d{1}-\\d{3}-\\d{3}$|^0[6|8|9]
            // \\d{1}-\\d{3}-\\d{4}$', 'g'),
            // message: 'กรุณากรอกหมายเลขโทรศัพท์ให้ตรงรูปแบบ', trigger: 'blur' },
          ],
          'address.fax': [
            { message: 'กรุณากรอกหมายเลขโทรสาร', trigger: 'blur' },
            // { type: 'string',
            // pattern: new XRegExp('^02-\\d{3}-\\d{4}$|^0[3-5|7]\\d{1}-\\d{3}-\\d{3}$', 'g'),
            // message: 'กรุณากรอกหมายเลขโทรสารให้ตรงรูปแบบ', trigger: 'blur' },
          ],
          bill_payment_comp_code: [
            { validator: this.checkBillPaymentFields, message: 'กรุณาเลือกอย่างน้อย 1 รายการ', trigger: 'blur' },
            { validator: this.checkBillPaymentCompCode, message: 'กรุณากรอก COMP CODE', trigger: 'blur' },
          ],
          bill_payment_account_no: [
            { validator: this.checkBillPaymentFields, message: 'กรุณาเลือกอย่างน้อย 1 รายการ', trigger: 'blur' },
            { validator: this.checkBillPaymentAccountNo, message: 'กรุณากรอกหมายเลขบัญชี', trigger: 'blur' },
          ],
          bill_payment_biller_id: [
            { type: 'string', min: 15, max: 15, message: 'กรุณากรอก Biller ID ความยาว 15 หลัก', trigger: 'blur' },
            { validator: this.checkBillPaymentFields, message: 'กรุณาเลือกอย่างน้อย 1 รายการ', trigger: 'blur' },
            { validator: this.checkBillPaymentBillerId, message: 'กรุณากรอก Biller ID', trigger: 'blur' },
          ],
          barcode_qrcode_biller_id_tax_id: [
            { type: 'string', min: 13, max: 13, message: 'กรุณากรอก Biller ID ความยาว 13 หลัก', trigger: 'blur' },
            { validator: this.checkBarcodeQRCodeBillerId, message: 'กรุณากรอก Biller ID และ Suffix 2 หลัก', trigger: 'blur' },
          ],
          barcode_qrcode_account_no: [
            { validator: this.checkBarcodeQRCodeAccountNo, message: 'กรุณากรอกหมายเลขบัญชี และ Suffix 2 หลัก', trigger: 'blur' },
          ],
          ir_br: [
            { validator: this.checkIBIR, message: 'กรุณากรอก IB/IR', trigger: 'blur' },
          ],
          account_name: [
            { required: true, message: 'กรุณาชื่อบัญชี', trigger: 'blur' },
          ],
          label_ref1: [
            { required: true, message: 'กรุณากรอกชื่อเรียก Ref. 1', trigger: 'blur' },
          ],
          label_ref2: [
            { required: true, message: 'กรุณากรอกชื่อเรียก Ref. 2', trigger: 'blur' },
          ],
          notification_msg: [
            { validator: this.checkNotificationMsg, message: 'กรุณากรอกข้อความแจ้งเตือน', trigger: 'blur' },
          ],
          /* // Other Banks
          'other_banks.account_name': [
            { required: true, message: 'กรุณากรอกชื่อบัญชี', trigger: 'blur' },
          ],
          'other_banks.account_number': [
            { required: true, message: 'กรุณากรอกเลขที่บัญชี', trigger: 'blur' },
          ], */
        },
        profileForm: {
          id: null,
          name: '',
          use_company_address: true,
          address: {
            line_1: '',
            district: '',
            city: '',
            province: '',
            postal_code: '',
            tel: '',
            fax: '',
          },
          use_bill_payment_comp_code: false,
          bill_payment_comp_code: '',
          use_bill_payment_account_no: false,
          bill_payment_account_no: '',
          use_bill_payment_biller_id: true,
          bill_payment_biller_id: '',
          barcode_qrcode_type: 'biller_id',
          barcode_qrcode_biller_id_tax_id: '',
          barcode_qrcode_biller_id_suffix: '',
          barcode_qrcode_account_no: '',
          barcode_qrcode_account_no_suffix: '',
          payment_type: 'CHEQUE_OR_CASH', // "CHEQUE_OR_CASH", "CASH_ONLY"
          charge_payer: false,
          ir_br: '',
          account_name: '',
          label_ref1: 'Ref. 1',
          label_ref2: 'Ref. 2',
          label_ref3: 'Ref. 3',
          allow_other_banks: false,
          other_banks: [],
          notification_msg_type: 'CUSTOM', // DATE or CUSTOM
          notification_msg_date: '1',
          notification_msg_custom: '',
          notification_msg: '',
          logo: '',
          printing_option: 'COLOR', // "COLOR", "BLACK_AND_WHITE"
        },
        mask: {
          compCode: {
            mask: /^\d{0,4}$/,
          },
          accountNo: {
            mask: '000-0-00000-0',
          },
          billerId: {
            mask: /^\d{0,15}$/,
          },
          postcode: {
            mask: '00000',
          },
          telMask: [
            { mask: '{02}-000-0000' },
            { mask: '{0}00-000-000' },
            { mask: '{0}00-000-0000' },
          ],
          faxMask: [
            { mask: '{02}-000-0000' },
            { mask: '{0}00-000-000' },
          ],
          numberOnly: {
            mask: /^\d+/,
          },
          accNumberOnly: {
            mask: '0000000000',
          },
        },
        handleOtherBankLogoSuccessList: [
          this.handleOtherBankLogo1Success,
          this.handleOtherBankLogo2Success,
          this.handleOtherBankLogo3Success,
          this.handleOtherBankLogo4Success,
          this.handleOtherBankLogo5Success,
          this.handleOtherBankLogo6Success,
        ],
      };
    },
    computed: {
      isAuthenticated() {
        return this.$store.getters.idToken;
      },
      isBillerAdmin() {
        if (this.$store.getters.myAdminInfo) {
          return this.$store.getters.myAdminInfo.role === 'ADMIN';
        }
        return false;
      },
      company() {
        return this.$store.getters.billerInfo;
      },
      companyId() {
        return this.company.id;
      },
      profile() {
        return this.$store.getters.currentBillerProfile;
      },
      uploadUrl() {
        return `${process.env.CLOUDINARY_API_BASE_URL}${window.appConfig.CLOUDINARY_CLOUD_NAME}/upload`;
      },
      provinceList() {
        return helpers.getProvinceList();
      },
      notificationDayList() {
        return this.dayList;
      },
      profileBusinessType() {
        if (this.profile) {
          if (this.profile.business_type === 'B2C') {
            return 'B2C';
          } else if (this.profile.business_type === 'B2B') {
            return 'ธุรกิจ B2B';
          } else if (this.profile.business_type === 'EDU') {
            return 'สถานศึกษา';
          } else if (this.profile.business_type === 'SPECIAL') {
            const businessText = this.profile.business_type_special_industry || '';
            return `ธุรกิจพิเศษ: ${businessText}`;
          }
        }
        return '';
      },
    },
    methods: {
      goBack() {
        this.$router.push({
          name: 'BillerProfileDetail',
          params: {
            profileId: this.profileId,
          },
        });
      },
      populateAddressFromCompany(isCopy) {
        if (isCopy) {
          Object.keys(this.company.address).forEach((key) => {
            const val = isCopy ? this.company.address[key] : '';
            this.$set(this.profileForm.address, key, val);
          });
        }
        /* if (isCopy) {
          this.$set(this.profileForm.address, 'tel', this.company.tel);
          this.$set(this.profileForm.address, 'fax', this.company.fax);
        } */
        this.updatePostCode();
      },
      toggleUsingCompanyAddress() {
        this.populateAddressFromCompany(this.profileForm.use_company_address);
      },
      handleBillPaymentData() {
        if (this.profileForm.use_bill_payment_biller_id) {
          this.$refs.profileForm.validateField('bill_payment_biller_id');
        } else {
          // this.profileForm.bill_payment_biller_id = '';
          this.$refs.bill_payment_biller_id.resetField();
        }
        if (this.profileForm.use_bill_payment_comp_code) {
          this.$refs.profileForm.validateField('bill_payment_comp_code');
        } else {
          // this.profileForm.bill_payment_comp_code = '';
          this.$refs.bill_payment_comp_code.resetField();
        }
        if (this.profileForm.use_bill_payment_account_no) {
          this.$refs.profileForm.validateField('bill_payment_account_no');
        } else {
          // this.profileForm.bill_payment_account_no = '';
          this.$refs.bill_payment_account_no.resetField();
        }
        if (!this.profileForm.use_bill_payment_comp_code &&
          !this.profileForm.use_bill_payment_account_no &&
          !this.profileForm.use_bill_payment_biller_id) {
          this.$refs.profileForm.validateField('bill_payment_biller_id');
          this.$refs.profileForm.validateField('bill_payment_comp_code');
          this.$refs.profileForm.validateField('bill_payment_account_no');
        }
      },
      toggleUsingCompCode() {
        this.handleBillPaymentData();
      },
      toggleUsingAccountNo() {
        this.handleBillPaymentData();
      },
      toggleUsingBillerId() {
        this.handleBillPaymentData();
        if (this.profileForm.use_bill_payment_biller_id) {
          this.profileForm.barcode_qrcode_type = 'biller_id';
          this.changeBillPaymentBillerId();
        }
      },
      toggleUsingBarcodeQRcodeType() {
        if (this.profileForm.barcode_qrcode_type === 'biller_id') {
          this.profileForm.barcode_qrcode_account_no = '';
          this.profileForm.barcode_qrcode_account_no_suffix = '';
        } else {
          this.profileForm.barcode_qrcode_biller_id_tax_id = '';
          this.profileForm.barcode_qrcode_biller_id_suffix = '';
        }
        this.$refs.profileForm.validateField('barcode_qrcode_biller_id_tax_id');
        this.$refs.profileForm.validateField('barcode_qrcode_account_no');
      },
      changeBillPaymentBillerId() {
        if (this.profileForm.barcode_qrcode_type === 'biller_id') {
          if (this.profileForm.use_bill_payment_biller_id) {
            // update paired biller_id
            const billerId = this.profileForm.bill_payment_biller_id;
            this.profileForm.barcode_qrcode_biller_id_tax_id = billerId;
          }
          // this.profileForm.barcode_qrcode_account_no = '';
          // this.profileForm.barcode_qrcode_account_no_suffix = '';
          this.$refs.barcode_qrcode_account_no.resetField();
        } else {
          // this.profileForm.barcode_qrcode_biller_id_tax_id = '';
          // this.profileForm.barcode_qrcode_biller_id_suffix = '';
          this.$refs.barcode_qrcode_biller_id_tax_id.resetField();
        }
      },
      checkBillPaymentFields(rule, value, cb) {
        // check at least one of 'bill_payment_comp_code',
        // 'bill_payment_account_no', 'bill_payment_biller_id'
        if (!this.profileForm.use_bill_payment_comp_code &&
          !this.profileForm.use_bill_payment_account_no &&
          !this.profileForm.use_bill_payment_biller_id) {
          cb(new Error('required bill payment information'));
        } else {
          cb();
        }
      },
      checkBillPaymentCompCode(rule, value, cb) {
        if (this.profileForm.use_bill_payment_comp_code &&
          (this.profileForm.bill_payment_comp_code &&
            this.profileForm.bill_payment_comp_code.trim() === '')) {
          cb(new Error('COMP CODE is required'));
        } else {
          cb();
        }
      },
      checkBillPaymentAccountNo(rule, value, cb) {
        if (this.profileForm.use_bill_payment_account_no &&
          (this.profileForm.bill_payment_account_no &&
            this.profileForm.bill_payment_account_no.trim() === '')) {
          cb(new Error('Account No. is required'));
        } else {
          cb();
        }
      },
      checkBillPaymentBillerId(rule, value, cb) {
        if (this.profileForm.use_bill_payment_biller_id &&
          (this.profileForm.bill_payment_biller_id &&
            this.profileForm.bill_payment_biller_id.trim() === '')) {
          cb(new Error('Biller ID is required'));
        } else {
          cb();
        }
      },
      checkBarcodeQRCodeBillerId(rule, value, cb) {
        if (this.profileForm.barcode_qrcode_type === 'biller_id') {
          if (this.profileForm.barcode_qrcode_biller_id_tax_id &&
            this.profileForm.barcode_qrcode_biller_id_tax_id.trim() === '') {
            cb(new Error('Biller ID is required'));
          } else if (this.profileForm.barcode_qrcode_biller_id_suffix &&
            this.profileForm.barcode_qrcode_biller_id_suffix.trim() === '') {
            cb(new Error('Biller ID Suffix is required'));
          } else if (this.profileForm.barcode_qrcode_biller_id_suffix &&
            this.profileForm.barcode_qrcode_biller_id_suffix.length !== 2) {
            cb(new Error('Biller ID Suffix must be 2 digits'));
          } else {
            cb();
          }
        } else {
          cb();
        }
      },
      checkBarcodeQRCodeAccountNo(rule, value, cb) {
        if (this.profileForm.barcode_qrcode_type === 'account_no') {
          if (this.profileForm.barcode_qrcode_account_no &&
            this.profileForm.barcode_qrcode_account_no.trim() === '') {
            cb(new Error('Account No. is required'));
          } else if (this.profileForm.barcode_qrcode_account_no_suffix &&
            this.profileForm.barcode_qrcode_account_no_suffix.trim() === '') {
            cb(new Error('Account No. Suffix is required'));
          } else if (this.profileForm.barcode_qrcode_account_no_suffix &&
            this.profileForm.barcode_qrcode_account_no_suffix.length !== 2) {
            cb(new Error('Account No. Suffix must be 2 digits'));
          } else {
            cb();
          }
        } else {
          cb();
        }
      },
      checkChargePayer() {
        if (this.profileForm.charge_payer) {
          this.$refs.profileForm.validateField('ir_br');
        }
      },
      checkIBIR(rule, value, cb) {
        if (this.profileForm.charge_payer && this.profileForm.ir_br.trim() === '') {
          cb(new Error('IB/IR is required'));
        }
        cb();
      },
      checkNotificationMsg(rule, value, cb) {
        // check if custom message is select and not blank
        this.updateNotificationMsgValue();
        /* if (this.profileForm.notification_msg_type === 'CUSTOM' &&
          this.profileForm.notification_msg_custom.trim() === '') {
          cb(new Error('Custom Notification message is required'));
        } */
        cb();
      },
      checkOtherBankItem(rule, value, cb) {
        const fieldLabels = {
          account_name: 'ชื่อบัญชี',
          account_number: 'หมายเลขบัญชี',
        };
        const fieldItem = rule.fullField.split('.');
        if (fieldItem.length === 3) {
          const field = fieldItem[0];
          const index = fieldItem[1];
          const itemField = fieldItem[2];
          const bankInfo = this.profileForm[field][index];
          const isActive = bankInfo.is_active;
          if (isActive) {
            if (itemField === 'account_name' && bankInfo[itemField].trim() === '') {
              cb(new Error(`กรุณากรอก${fieldLabels[itemField]}`));
            }
            if (itemField === 'account_number' &&
              bankInfo[itemField].trim() !== '' &&
              bankInfo[itemField].length !== 10) {
              cb(new Error(`กรุณากรอก${fieldLabels[itemField]}ให้ครบ 10 หลัก`));
            }
            if (this.isBBLBank(bankInfo.account_name)) {
              if (itemField === 'service_code' && bankInfo.service_code.trim() === '') {
                cb(new Error(`กรุณากรอก Service Code`));
              }
            }
          }
        }
        cb();
      },
      toggleUsingNotificationMsgValue() {
        // update notification_msg
        // this.updateNotificationMsgValue();
        if (this.profileForm.notification_msg_type === 'DATE') {
          this.profileForm.notification_msg_custom = '';
        }
        this.$refs.profileForm.validateField('notification_msg');
      },
      updateNotificationMsgValue() {
        if (this.profileForm.notification_msg_type === 'CUSTOM') {
          this.profileForm.notification_msg = this.profileForm.notification_msg_custom;
        } else {
          this.profileForm.notification_msg = `กรุณาชำระภายในวันที่ ${this.profileForm.notification_msg_date} ของทุกเดือน`;
        }
      },
      handleLogoSuccess(res) {
        this.isUploading = false;
        if (res.secure_url) {
          this.profileForm.logo = res.secure_url;
        }
      },
      handleOtherBankLogo1Success(res) {
        this.handleOtherBankLogoSuccess(0, res);
      },
      handleOtherBankLogo2Success(res) {
        this.handleOtherBankLogoSuccess(1, res);
      },
      handleOtherBankLogo3Success(res) {
        this.handleOtherBankLogoSuccess(2, res);
      },
      handleOtherBankLogo4Success(res) {
        this.handleOtherBankLogoSuccess(3, res);
      },
      handleOtherBankLogo5Success(res) {
        this.handleOtherBankLogoSuccess(4, res);
      },
      handleOtherBankLogo6Success(res) {
        this.handleOtherBankLogoSuccess(5, res);
      },
      handleOtherBankLogoSuccess(index, res) {
        this.isUploading = false;
        if (res.secure_url) {
          this.profileForm.other_banks[index].account_logo = res.secure_url;
        }
      },
      handleLogoFail(res) {
        this.isUploading = false;
        let errorMessage = this.$t('Unexpected error');
        if (res) {
          errorMessage = res;
          if (res.error) {
            errorMessage = res.error;
            if (res.error.message) {
              errorMessage = res.error.message;
            }
          }
        }
        this.$store.dispatch('ADD_ERROR_FLASH', errorMessage);
      },
      beforeLogoUpload(file) {
        const isImage = ['image/jpeg', 'image/png', 'image/gif'].indexOf(file.type) !== -1;
        const isLt2M = file.size / 1024 / 1024 < 2;

        if (!isImage) {
          this.$message.error('Logo picture must be JPG, PNG or GIF format!');
        }
        if (!isLt2M) {
          this.$message.error('Logo picture size can not exceed 2MB!');
        }
        return isImage && isLt2M;
      },
      handleHttpRequest(options) {
        this.isUploading = true;
        return this.$store.dispatch('UPLOAD_BILLER_COMPANY_LOGO', options.file);
      },
      handleOtherBankHttpRequest(options) {
        console.log(options);
        this.isUploading = true;
        return this.$store.dispatch('UPLOAD_BILLER_OTHER_BANK_LOGO', options.file);
      },
      buildPayload() {
        const payload = JSON.parse(JSON.stringify(this.profileForm));
        // remove unnecessary key
        const stripKeys = [
          // 'use_company_address',
          'use_bill_payment_comp_code',
          'use_bill_payment_account_no',
          'use_bill_payment_biller_id',
          'barcode_qrcode_type',
          'notification_msg_type',
          'notification_msg_date',
          'notification_msg_custom',
          // 'allow_other_banks',
        ];
        const makeNullKeys = [
          'bill_payment_comp_code',
          'bill_payment_account_no',
          'bill_payment_biller_id',
          'barcode_qrcode_biller_id_tax_id',
          'barcode_qrcode_biller_id_suffix',
          'barcode_qrcode_account_no',
          'barcode_qrcode_account_no_suffix',
          'notification_msg',
        ];
        Object.keys(payload).forEach((key) => {
          if (stripKeys.indexOf(key) !== -1) {
            delete payload[key];
          }
          if (key === 'use_company_address') {
            payload.use_company_address = payload.use_company_address || false;
          }
          // transform data before sending
          if (makeNullKeys.indexOf(key) !== -1) {
            if (payload[key] === '') {
              payload[key] = null;
            }
          }
        });
        payload.other_banks.forEach((bankInfo) => {
          delete bankInfo.is_active;
          if (!this.isBBLBank(bankInfo.account_name)) {
            bankInfo.service_code = '';
            bankInfo.branch_code = '';
          }
        });
        return payload;
      },
      submitForm(formName) {
        this.$refs[formName].validate(this.validateForm);
      },
      validateForm(valid) {
        if (!valid) {
          this.$log.error('error submit!!');
          return false;
        }
        // build payload first
        this.updateNotificationMsgValue();
        this.isSubmitting = true;
        const payload = this.buildPayload();
        // transform notification_msg
        if (payload.notification_msg) {
          payload.notification_msg = payload.notification_msg.replace(new XRegExp('[ ]{2,}', 'g'), ' ');
        }
        const reqPayload = {
          profileId: payload.id,
          payload,
        };
        this.$store.dispatch('UPDATE_BILLER_PROFILE_INFO', reqPayload).then(() => {
          this.isSubmitting = false;
          this.$message({
            type: 'success',
            showClose: true,
            duration: 10000,
            message: 'แก้ไขข้อมูลสำหรับรับชำระเงินของบริษัทสำเร็จแล้ว',
          });
          this.$nextTick(() => {
            this.goBack();
          });
        }).catch((msg) => {
          this.isSubmitting = false;
          if (msg) {
            this.$store.dispatch('ADD_ERROR_FLASH', this.$t(msg));
          } else {
            this.$store.dispatch('ADD_ERROR_FLASH', this.$t('Unexpected error'));
          }
        });
        return true;
      },
      setupUpdateData() {
        this.isSetupUpdateData = true;
        Object.keys(this.profile).forEach((key) => {
          if (key === 'address') {
            // let isUseCompanyAddress = true;
            Object.keys(this.profile.address).forEach((addrKey) => {
              // check if the value for this profile & company are the same or not
              /* if (this.profile.address[addrKey] !== this.company.address[addrKey]) {
                isUseCompanyAddress = false;
              } */
              this.$set(this.profileForm.address, addrKey, this.profile.address[addrKey]);
            });
            // this.use_company_address = isUseCompanyAddress;
          } else if (key === 'notification_msg') {
            if (this.profile[key] === null) {
              this.$set(this.profileForm, key, '');
            } else {
              this.$set(this.profileForm, key, this.profile[key]);
            }
          } else if (key === 'other_banks') {
            // loop through array of other bank info
            this.profile[key].forEach((bankInfo) => {
              this.profileForm[key].push({
                is_active: bankInfo.account_name.trim() !== '',
                account_name: bankInfo.account_name,
                account_number: bankInfo.account_number,
                account_logo: bankInfo.account_logo,
                comcode: bankInfo.comcode,
                service_fee: bankInfo.service_fee,
                service_code: bankInfo.service_code,
                branch_code: bankInfo.branch_code,
              });
            });
            // check if other_banks is 6 yet?
            const addOtherBanksRowCount = 6 - this.profileForm.other_banks.length;
            for (let rowCount = 0; rowCount < addOtherBanksRowCount; rowCount += 1) {
              this.profileForm[key].push({
                is_active: false,
                account_name: '',
                account_number: '',
                account_logo: '',
                comcode: '',
                service_fee: '',
                service_code: '',
                branch_code: '',
              });
            }
          } else {
            this.$set(this.profileForm, key, this.profile[key]);
          }
        });
        // update required control fields' value
        this.profileForm
          .use_bill_payment_comp_code = !!this.profileForm.bill_payment_comp_code;
        this.profileForm
          .use_bill_payment_account_no = !!this.profileForm.bill_payment_account_no;
        this.profileForm
          .use_bill_payment_biller_id = !!this.profileForm.bill_payment_biller_id;
        this.profileForm
          .barcode_qrcode_type = this.profileForm.barcode_qrcode_biller_id_tax_id ? 'biller_id' : 'account_no';
        this.profileForm.notification_msg_type = this.profileForm
          .notification_msg.indexOf('กรุณาชำระภายในวันที่') !== -1 &&
        this.profileForm.notification_msg.indexOf('ของทุกเดือน') !== -1 ? 'DATE' : 'CUSTOM';
        if (this.profileForm.notification_msg_type === 'DATE') {
          if (this.profileForm.notification_msg) {
            this.profileForm
              .notification_msg_date = this.profileForm.notification_msg
                .replace('กรุณาชำระภายในวันที่', '')
                .replace('ของทุกเดือน', '');
          } else {
            this.profileForm.notification_msg_date = 1;
          }
        } else {
          this.profileForm.notification_msg_custom = this.profileForm.notification_msg;
        }
        this.updatePostCode();
        // FIX ME - Remove
        /* this.profileForm.allow_other_banks = true;
        for (let rowCount = 0; rowCount < 6; rowCount += 1) {
          this.profileForm.other_banks.push({
            is_active: false,
            account_name: '',
            account_number: '',
            account_logo: '',
            comcode: '',
            service_fee: '',
            service_code: '',
            branch_code: '',
          });
        } */
        // END FIX ME
      },
      clearUpdateData() {
        this.isSetupUpdateData = false;
        this.profileForm.id = null;
        this.profileForm.name = '';
        this.profileForm.use_company_address = true;
        this.profileForm.address.line_1 = '';
        this.profileForm.address.district = '';
        this.profileForm.address.city = '';
        this.profileForm.address.province = '';
        this.profileForm.address.postal_code = '';
        this.profileForm.address.tel = '';
        this.profileForm.address.fax = '';
        this.profileForm.use_bill_payment_comp_code = false;
        this.profileForm.bill_payment_comp_code = '';
        this.profileForm.use_bill_payment_account_no = false;
        this.profileForm.bill_payment_account_no = '';
        this.profileForm.use_bill_payment_biller_id = true;
        this.profileForm.bill_payment_biller_id = '';
        this.profileForm.barcode_qrcode_type = 'biller_id';
        this.profileForm.barcode_qrcode_biller_id_tax_id = '';
        this.profileForm.barcode_qrcode_biller_id_suffix = '';
        this.profileForm.barcode_qrcode_account_no = '';
        this.profileForm.barcode_qrcode_account_no_suffix = '';
        this.profileForm.payment_type = 'CHEQUE_OR_CASH';
        this.profileForm.charge_payer = false;
        this.profileForm.ir_br = '';
        this.profileForm.account_name = '';
        this.profileForm.label_ref1 = 'Ref. 1';
        this.profileForm.label_ref2 = 'Ref. 2';
        this.profileForm.label_ref3 = 'Ref. 3';
        this.profileForm.allow_other_banks = false;
        this.profileForm.other_banks.splice(0);
        this.profileForm.notification_msg_type = 'CUSTOM';
        this.profileForm.notification_msg_date = '1';
        this.profileForm.notification_msg_custom = '';
        this.profileForm.notification_msg = '';
        this.profileForm.logo = '';
        this.profileForm.printing_option = 'COLOR';
        this.useDefaultSCBNoti = true;
      },
      removeLogo() {
        this.$confirm('คุณต้องการยืนยันการลบโลโก้บริษัทออกใช่หรือไม่?', 'ยืนยันการลบโลโก้บริษัท', {
          confirmButtonText: 'ยืนยันการลบ',
          confirmButtonClass: 'danger-btn',
          cancelButtonText: 'ยกเลิก',
          type: 'warning',
        }).then(() => {
          this.profileForm.logo = '';
        }).catch(() => {
        });
      },
      removeOtherBankLogo(index) {
        this.$confirm('คุณต้องการยืนยันการลบโลโก้ธนาคารอื่นออกใช่หรือไม่?', 'ยืนยันการลบโลโก้ธนาคารอื่น', {
          confirmButtonText: 'ยืนยันการลบ',
          confirmButtonClass: 'danger-btn',
          cancelButtonText: 'ยกเลิก',
          type: 'warning',
        }).then(() => {
          this.profileForm.other_banks[index].account_logo = '';
        }).catch(() => {
        });
      },
      /* updateMaskTel() {
        this.maskValue(this.profileForm.address, 'tel', {
          mask: this.mask.telMask,
        });
      },
      updateMaskFax() {
        this.maskValue(this.profileForm.address, 'fax', {
          mask: this.mask.faxMask,
        });
      }, */
      updatePostCode() {
        try {
          this.maskValue(this.profileForm.address, 'postal_code', this.mask.postcode);
        } catch (e) {
          console.error(e);
        }
      },
      compCodeFormatter(val) {
        if (val) {
          return this.maskFormatter(val, this.mask.compCode);
        }
        return '-';
      },
      accountNoFormatter(val) {
        if (val) {
          return this.maskFormatter(val, this.mask.accountNo);
        }
        return '-';
      },
      billerIdFormatter(val) {
        if (val) {
          return this.maskFormatter(val, this.mask.billerId);
        }
        return '-';
      },
      maskValue(form, field, maskOptions) {
        const mask = createMask(maskOptions);
        const val = form[field];
        if (val) {
          const resolvedValue = mask.resolve(val.toString());
          form[field] = resolvedValue;
        }
      },
      unmaskValue(form, field, maskOptions) {
        const mask = createMask(maskOptions);
        const val = form[field];
        if (val) {
          mask.resolve(val.toString());
          form[field] = mask.unmaskedValue;
        }
      },
      maskFormatter(val, maskOptions) {
        const mask = createMask(maskOptions);
        if (val) {
          return mask.resolve(val.toString());
        }
        return '';
      },
      /* eslint prefer-arrow-callback: 0 */
      handleInput: _.debounce(function handleInputFn(evt, refName, maskName) {
        helpers.handleInput(evt,
          this.$refs, refName,
          this.mask, maskName);
      }, process.env.HANDLE_INPUT_DEBOUNCE_INTERVAL, { tailing: true }),
      isBBLBank(accountName) {
        const testString = accountName.toLowerCase();
        const isBBL = testString.indexOf('bbl') !== -1 ||
          testString.indexOf('bangkok') !== -1 ||
          testString.indexOf('กรุงเทพ') !== -1;
        return isBBL;
      },
      markBankInfoActive(index) {
        const bankInfo = this.profileForm.other_banks[index];
        if (!bankInfo.is_active) {
          // clear data
          bankInfo.account_name = '';
          bankInfo.account_number = '';
          bankInfo.account_logo = '';
          bankInfo.comcode = '';
          bankInfo.service_fee = '';
          bankInfo.service_code = '';
          bankInfo.branch_code = '';
        }
      },
    },
    mounted() {
      if (this.isUpdated) {
        setTimeout(() => {
          if (typeof this.profile === 'undefined' || this.profile === null) {
            this.goBack();
          }
        }, 1000);
      }
      this.clearUpdateData();
      // clear list
      this.dayList.splice(0);
      // populate day list
      for (let d = 1; d <= 31; d += 1) {
        this.dayList.push(d);
      }
      if (this.isUpdated && this.profile) {
        this.setupUpdateData();
      }
    },
    watch: {
      profile(newVal) {
        if (!this.isSetupUpdateData && this.isUpdated && newVal) {
          this.setupUpdateData();
        }
      },
    },
    beforeRouteEnter(to, from, next) {
      next((vm) => {
        vm.$store.dispatch('SELECT_CURRENT_BILLER_PROFILE', vm.profileId);
      });
    },
    beforeRouteLeave(to, from, next) {
      // clear current selection
      this.clearUpdateData();
      this.$store.dispatch('SELECT_CURRENT_BILLER_PROFILE', '');
      next();
    },
  };
  export default BillerProfileFormView;
</script>

<style lang="scss">
  @import '../../assets/styles/color';
  .profile-form {
    .el-select {
      width: 100%;
    }
    .cancel-btn, .terminate-btn {
      padding-top: 16px;
      padding-bottom: 16px;
    }
    .submit-button-group {
      float: right;
    }
    #profileForm\.notification_msg_type_1 {
      .el-radio__input {
        display: inline-block;
        line-height: 50px;
        float: left;
      }
      .el-radio__label {
        display: block;
        width: 500px;
        line-height: 50px;
        margin-left: 15px;
      }
    }
    #profileForm\.notification_msg_type_2 {
      .el-radio__label {
        display: none;
      }
    }
    .other-bank-info {
      .remove-logo {
        padding: 10px;
        font-size: 12px;
        width: 100%;
      }
    }
    .other-bank-logo-holder {
      width: 70px;
      height: 70px;
      border: 1px solid #ccc;
      background: #efefef;
      margin: 0 auto;

      img {
        max-width: 100%;
      }
    }
    .other-bank-logo-uploader {
      .el-upload-dragger {
        width: 70px;
        height: 70px;
        .logo-uploader-icon {
          margin: 8px 0;
          font-size: 50px;
        }
      }
    }
    .other-bank-upload-instruction {
      color: red;
      font-style: italic;
      margin-bottom: 20px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/biller/profile-form.vue